<template>
  <v-container class="fill-height">
    <v-row
      justify="center"
      align="center"
    >
      <v-col
        cols="12"
        sm="10"
        md="6"
        lg="5"
        xl="4"
      >
        <v-card
          max-width="440px"
          elevation="8"
          style="border-radius: 10px; margin: 0 auto;"
          outlined
        >
          <div class="progress-shell">
            <v-progress-linear
              :value="stepProgress"
              color="primary"
              height="4"
              background-opacity="0.12"
            />
          </div>

          <v-form
            @submit.prevent="registerUser($event)"
            ref="registerForm"
            v-model="valid"
          >
            <v-window v-model="step">
              <v-window-item :value="1">
                <v-card-text class="pa-10 pa-sm-12">
                  <div class="step-head mb-2">
                    <!-- Logo -->
                    <v-sheet
                      class="mx-auto mb-8"
                      width="60"
                      height="60"
                      elevation="0"
                    >
                      <v-img
                        :src="imageSource(get('config').logo)"
                        width="60"
                        height="60"
                        contain
                      ></v-img>
                    </v-sheet>
                    <div class="step-label text-caption grey--text text-uppercase">{{ $t('shop.register_form.step1_label') }}</div>
                    <div class="text-h5 font-weight-bold">{{ $t('shop.register_form.step1_title') }}</div>
                  </div>

                  <div class="intro-copy-block mb-8">
                    <p class="section-copy text-body-2 grey--text text--darken-1">
                      {{ $t('shop.register_form.step1_copy') }}
                    </p>
                  </div>

                  <div class="mb-5">
                    <div class="text-body-2  font-weight-medium mb-2">{{ $t('shop.register_form.email_label') }}</div>
                    <v-text-field
                      :rules="eMailRules"
                      validate-on-blur
                      name="email"
                      required
                      outlined
                      dense
                      :placeholder="$t('shop.register_form.email_placeholder')"
                      hide-details="auto"
                      style="border-radius: 10px;"
                    />
                  </div>

                  <div class="mb-5">
                    <div class="text-body-2 font-weight-medium mb-2">{{ $t('shop.register_form.password_label') }}</div>
                    <v-text-field
                      v-model="password"
                      name="password"
                      :rules="passwordRules"
                      :append-icon="showPwIcon ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                      @click:append="showPwIcon = !showPwIcon"
                      :type="showPwIcon ? 'text' : 'password'"
                      required
                      outlined
                      dense
                      :placeholder="$t('shop.register_form.password_placeholder')"
                      hide-details="auto"
                      loading
                      style="border-radius: 10px;"
                    >
                      <template v-slot:progress>
                        <v-progress-linear
                          :value="progress"
                          :color="color"
                          absolute
                          height="2"
                        />
                      </template>
                    </v-text-field>
                  </div>

                  <v-checkbox
                    class="mt-0 pt-0 mb-6"
                    name="Datenschutz"
                    v-model="datenschutzCheckbox"
                    required
                    :rules="[v => !!v || $t('shop.register_form.validation.consent_required')]"
                    hide-details="auto"
                    color="primary"
                    dense
                  >
                    <template v-slot:label>
                      <div class="text-body-2">
                        {{ $t('shop.register_form.privacy_consent') }}
                        <a
                          target="_blank"
                          @click.stop="linkTo('Nutzungsbedingungen')"
                          class="link-accent"
                        >{{ $t('shop.register_form.terms_link') }}</a>
                        {{ $t('common.and') }}
                        <a
                          target="_blank"
                          @click.stop="linkTo('Datenschutzerklaerung')"
                          class="link-accent"
                        >{{ $t('shop.register_form.privacy_link') }}</a>
                        {{ $t('shop.register_form.consent_agreement') }}
                      </div>
                    </template>
                  </v-checkbox>

                  <v-btn
                    color="primary"
                    width="100%"
                    large
                    type="submit"
                    :disabled="!valid"
                    :loading="loadingStep"
                    elevation="0"
                    class="text-none font-weight-bold"
                  >
                    {{ $t('shop.register_form.next') }}
                  </v-btn>
                </v-card-text>

                <div class="trust-row px-10 px-sm-12 pb-10 pb-sm-12">
                  <div class="trust-item">
                    <v-icon small>mdi-lock-outline</v-icon>
                    <span>{{ $t('shop.register_form.secure_protected') }}</span>
                  </div>
                  <div class="trust-item">
                    <v-icon small>mdi-shield-check-outline</v-icon>
                    <span>{{ $t('shop.register_form.data_stays_private') }}</span>
                  </div>
                </div>

              </v-window-item>

              <v-window-item :value="4">
                <v-card-text class="pa-10 pa-sm-12 text-center">
                  <div
                    class="result-state state-success"
                    v-if="succesfullCreated"
                  >
                    <v-icon
                      size="80"
                      color="success"
                      class="mb-6"
                    >mdi-check-circle-outline</v-icon>
                    <p class="text-body-2 grey--text text--darken-1">{{ $t('shop.register_form.registration_success') }}</p>
                    <div class="text-h5 font-weight-bold mb-2">{{ $t('shop.register_form.activation_mail_send') }}</div>
                    <p class="text-body-2 grey--text text--darken-1">
                      {{ $t('shop.register_form.success_email_hint') }}
                    </p>
                  </div>

                  <div
                    class="result-state state-error"
                    v-else
                  >
                    <v-icon
                      size="80"
                      color="error"
                      class="mb-6"
                    >mdi-alert-circle-outline</v-icon>
                    <div class="text-h5 font-weight-bold mb-2">{{ $t('shop.register_form.error_title') }}</div>
                    <p class="text-body-2 grey--text text--darken-1 mb-6">{{ errorMessage }}</p>
                    <v-btn
                      outlined
                      color="primary"
                      width="100%"
                      @click="step = 1"
                      class="text-none font-weight-bold"
                    >
                      {{ $t('shop.register_form.use_different_email') }}
                    </v-btn>
                  </div>
                </v-card-text>
              </v-window-item>
            </v-window>
          </v-form>
        </v-card>

        <div class="text-center mt-4 white--text">
          <span class="text-body-2">{{ $t('shop.register_form.has_account') }}</span>
          <a
            class="text-body-2 font-weight-bold text-decoration-none white--text ml-2"
            @click="linkTo('login')"
            style="cursor: pointer;"
          >{{ $t('shop.register_form.go_to_login') }}</a>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import APIService from '@/services/api.service';
import AppService from '@/services/app.service';
import { mapGetters } from 'vuex';

import registerForm from '@/components/register/registerForm';

export default {
  name: 'registerForm',
  components: {
    response: () => import('@/components/response/response'),
  },
  extends: registerForm,
  data() {
    return {
      step: 1,
      valid: true,
      loadingStep: false,
      password: '',
      showPwIcon: false,
      datenschutzCheckbox: false,
      errorMessage: '',
      succesfullCreated: false,
      eMailRules: [
        v => !!v || this.$t('shop.register_form.validation.email_required'),
        v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$t('shop.register_form.validation.email_invalid')
      ],
      passwordRules: [
        v => !!v || this.$t('shop.register_form.validation.password_required'),
        v => (v && v.length >= 6) || this.$t('shop.register_form.validation.password_short')
      ],
    }
  },

  computed: {
    ...mapGetters(["get"]),
    progress() {
      return Math.min(100, this.password.length * 16.6667)
    },
    color() {
      return ['error', 'warning', 'success'][Math.floor(this.progress / 50)]
    },
    stepProgress() {
      const map = { 1: 25, 2: 50, 3: 75, 4: 100 }
      return map[this.step] || 0
    },
  },

  methods: {

    registerUser(e) {

      this.loadingStep = true
      var payload = APIService.getFormData(APIService.getForm(e))

      this.$store.dispatch('post',
        {
          endpoint: '/api/v1/users',
          event: payload,
          header: false,
          message: false,
          state: 'user'
        }
      ).then((response) => {
        this.succesfullCreated = true;
        this.loadingStep = false;
        this.step = 4;

      }).catch((error) => {
        this.loadingStep = false;
        this.succesfullCreated = false;

        if (error.response) {
          const { status, data } = error.response;

          let apiErrorMessage = '';
          if (data && data.message) {
            apiErrorMessage = data.message;
          } else if (data && data.error) {
            apiErrorMessage = data.error;
          } else if (data && typeof data === 'string') {
            apiErrorMessage = data;
          }

          // Bekannte Statuscodes: Text aus dem i18n-Set.
          // Die Server-Message ist unuebersetzt (Validator-/Symfony-Default) und dient nur als Fallback.
          if (status === 422) {
            this.errorMessage = this.$t('shop.register_form.error.email_taken');
          } else if (status === 400) {
            this.errorMessage = this.$t('shop.register_form.error.invalid_input');
          } else if (status === 500) {
            this.errorMessage = this.$t('shop.register_form.error.server_error');
          } else if (status === 503) {
            this.errorMessage = this.$t('shop.register_form.error.email_server_inactive');
          } else {
            this.errorMessage = apiErrorMessage || this.$t('shop.register_form.error.unknown', { status });
          }

          APIService.setResponse({
            message: this.errorMessage,
            color: "red"
          });
        } else if (error.request) {
          this.errorMessage = this.$t('shop.register_form.error.no_connection');
          APIService.setResponse({
            message: this.errorMessage,
            color: "red"
          });
        } else {
          this.errorMessage = error.message || this.$t('shop.register_form.error.unexpected');
          APIService.setResponse({
            message: this.errorMessage,
            color: "red"
          });
        }
        this.step = 4;
      })
    },

    linkTo(route) {
      this.$router.push('/' + route).catch(() => {
        APIService.setResponse({ message: "Hier bist du bereits", color: "blue" })
      });
    },

    imageSource(item, width, height) {
      return AppService.imageSource(item, width, height);
    },
  },
}
</script>

<style scoped>
.progress-shell {
  overflow: hidden;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.step-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.step-label {
  letter-spacing: 1px;
}



.section-copy {
  margin: 0;
  text-align: center;
}

.intro-copy-block {
  max-width: 48ch;
  margin: 0 auto;
  text-align: center;
}

.trust-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-size: 13px;
  line-height: 1.4;
  opacity: 0.72;
}

.trust-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}



.result-state {
  min-height: 180px;
  padding: 16px;
  text-align: center;
}

.result-state p {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.78;
}

.link-accent {
  color: #00C3FF;
  text-decoration: none;
}

:deep(.v-card__text) {
  word-break: normal;
}

:deep(.v-card__actions) {
  justify-content: center;
}

:deep(.v-input__slot) {
  border-radius: 10px !important;
  box-shadow: none !important;
}

:deep(.v-text-field--outlined fieldset) {
  border-width: 1px;
}

:deep(.v-alert) {
  border-radius: 10px;
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 22px;
  }
}
</style>
